import { useEffect, useState } from 'react';
import { automaticStore, vendorNativeUrl, vendorWebUrl } from '../lib/vendorGateway';
import { StoreBadge } from './StoreBadge';
import type { GatewayContext, RoutingFlags } from '../lib/vendorGateway';

export function VendorGateway({ context }: { context: GatewayContext; flags?: RoutingFlags }) {
	const [redirect, setRedirect] = useState(() => automaticStore(context));
	const { code, nativeReady, device, legacy } = context;
	useEffect(() => {
		document.title = 'Open ScanDeck Lead Capture';
		if (!redirect) return;
		const cancel = () => setRedirect(null);
		const hidden = () => { if (document.hidden) cancel(); };
		const timer = window.setTimeout(() => {
			if (!document.hidden) window.location.replace(redirect);
		}, 7000);
		window.addEventListener('pagehide', cancel);
		document.addEventListener('visibilitychange', hidden);
		return () => {
			window.clearTimeout(timer);
			window.removeEventListener('pagehide', cancel);
			document.removeEventListener('visibilitychange', hidden);
		};
	}, [redirect]);

	return (
		<main className="min-h-dvh bg-bg px-5 py-8 text-text sm:py-16" onPointerDown={() => setRedirect(null)} onKeyDown={() => setRedirect(null)}>
			<div className="mx-auto max-w-lg">
				<img src="/scandeck-logo.svg" alt="ScanDeck" className="mb-12 w-48" />
				<p className="mb-3 text-sm font-semibold uppercase tracking-widest text-accent">Lead Capture</p>
				<h1 className="font-display text-4xl font-bold leading-tight sm:text-5xl">
					{code ? 'Your next connection starts here.' : 'This card link is incomplete.'}
				</h1>
				<p className="mt-5 text-lg text-muted">
					{code
						? 'Open ScanDeck on your phone to capture leads at your event.'
						: 'Scan your vendor card again, or ask the event organizer for a new card.'}
				</p>

				{code && (
					<>
						<div className="my-8 rounded-xl border border-border bg-panel px-5 py-4">
							<p className="text-sm text-muted">Your vendor code</p>
							<p className="mt-1 break-all font-mono text-2xl font-semibold tracking-wider">{code}</p>
							<p className="mt-2 text-sm text-muted">Use the PIN printed on your card to sign in.</p>
						</div>
						<div className="flex flex-col gap-3">
							{nativeReady && <a className="btn-primary" href={vendorNativeUrl(code)} onClick={() => setRedirect(null)}>
								Open ScanDeck
							</a>}
							<div className="flex flex-wrap gap-3">
								{device !== 'android' && <StoreBadge store="ios" height={52} />}
								{device !== 'ios' && <StoreBadge store="android" height={52} />}
							</div>
						</div>
						{legacy && nativeReady && <p className="mt-4 text-sm text-muted">
							Using an older printed card? Tap Open ScanDeck to continue in the app.
						</p>}
						<div className={redirect ? 'mt-6' : ''} aria-live="polite">
							{redirect && <p className="text-sm text-muted">
								Opening {device === 'ios' ? 'the App Store' : 'Google Play'} shortly.{' '}
								<button className="underline" onClick={() => setRedirect(null)}>Stay here</button>
							</p>}
						</div>
						<section className="border-t border-divider pt-6">
							<h2 className="font-semibold">Installing for the first time?</h2>
							<p className="mt-2 text-sm leading-relaxed text-muted">
								Install ScanDeck, then scan this card again to fill in your vendor code.
								You can also type the code and PIN from your card in the app.
							</p>
							<a className="mt-5 inline-block text-sm font-medium text-accent underline"
								href={vendorWebUrl(code)} rel="noreferrer" onClick={() => setRedirect(null)}>
								Continue in browser
							</a>
						</section>
					</>
				)}
				<footer className="mt-10 text-sm text-muted">
					Need help? <a className="underline" href="mailto:info@scandeckapp.com">info@scandeckapp.com</a>
				</footer>
			</div>
		</main>
	);
}
